<template>
  <div class="container py-4">
    <h1 class="mb-4">Email Templates Review</h1>

    <LoadingIndicatorComponent
      v-if="isLoading"
      message="Loading email templates..."
    />

    <div v-else-if="error" class="alert alert-danger">
      <strong>Error:</strong> {{ error }}
    </div>

    <div v-else>
      <div class="row mb-4">
        <div class="col-md-6">
          <label class="form-label fw-semibold">Select Template</label>
          <select v-model="selectedTemplateKey" class="form-select">
            <option value="">-- Select a template --</option>
            <option
              v-for="template in uniqueTemplates"
              :key="`${template.component}-${template.name}`"
              :value="`${template.component}-${template.name}`"
            >
              {{ template.component }} / {{ template.name }}
            </option>
          </select>
        </div>

        <div class="col-md-6">
          <label class="form-label fw-semibold">Select Language</label>
          <div class="btn-group w-100" role="group">
            <button
              v-for="lang in availableLanguagesForCurrentTemplate"
              :key="lang"
              type="button"
              class="btn"
              :class="
                selectedLanguage === lang
                  ? 'btn-primary'
                  : 'btn-outline-primary'
              "
              @click="selectedLanguage = lang"
            >
              {{ lang.toUpperCase() }}
            </button>
          </div>
          <small
            v-if="!availableLanguagesForCurrentTemplate.length"
            class="text-muted"
          >
            Select a template to see available languages
          </small>
        </div>
      </div>

      <!-- Transaction Notification Specific Fields -->
      <div
        v-if="isTransactionNotificationTemplate"
        class="row mb-4 border-top pt-4"
      >
        <div class="col-md-6">
          <label class="form-label fw-semibold">Select Transaction</label>
          <select
            v-model="selectedTransactionId"
            class="form-select"
            :disabled="isLoadingTransactions"
          >
            <option value="">-- Select a transaction --</option>
            <option
              v-for="transaction in transactions"
              :key="transaction.id"
              :value="transaction.id"
            >
              {{ formatTransactionDate(transaction.date) }} - Transaction #{{
                transaction.id
              }}
              ({{ transaction.num_companies }} companies, €{{
                (transaction.total_amount || 0).toFixed(2)
              }})
            </option>
          </select>
          <div
            v-if="isLoadingTransactions"
            class="spinner-border spinner-border-sm text-primary mt-2"
            role="status"
          >
            <span class="visually-hidden">Loading transactions...</span>
          </div>
        </div>

        <div class="col-md-6">
          <label class="form-label fw-semibold">Select Account</label>
          <select
            v-model="selectedAccountId"
            class="form-select"
            :disabled="isLoadingAccounts"
          >
            <option value="">-- Select an account --</option>
            <option
              v-for="account in accounts"
              :key="account.id"
              :value="account.id"
            >
              {{ account.name }} ({{ account.email }})
            </option>
          </select>
          <div
            v-if="isLoadingAccounts"
            class="spinner-border spinner-border-sm text-primary mt-2"
            role="status"
          >
            <span class="visually-hidden">Loading accounts...</span>
          </div>
        </div>
      </div>

      <!-- Buyer Sales Invoice Specific Fields -->
      <div v-if="isBuyerSalesInvoiceTemplate" class="row mb-4 border-top pt-4">
        <div class="col-md-6">
          <label for="buyerInvoiceSelect" class="form-label fw-semibold"
            >Select Buyer Invoice</label
          >
          <select
            id="buyerInvoiceSelect"
            v-model="selectedBuyerInvoiceId"
            class="form-select"
            :disabled="isLoadingBuyerInvoices"
          >
            <option value="">-- Sample data --</option>
            <option
              v-for="invoice in buyerInvoices"
              :key="invoice.id"
              :value="invoice.id"
            >
              {{ formatTransactionDate(invoice.created_at) }} -
              {{ invoice.invoice_number }} -
              {{ invoice.buyer_name }} (Transaction #{{
                invoice.sales_transaction_id
              }}){{ invoice.is_canceled ? " - CANCELED" : "" }}
            </option>
          </select>
          <div
            v-if="isLoadingBuyerInvoices"
            class="spinner-border spinner-border-sm text-primary mt-2"
            role="status"
          >
            <span class="visually-hidden">Loading buyer invoices...</span>
          </div>
        </div>
      </div>

      <!-- Fee Invoice Notification Specific Fields -->
      <FeeInvoicePreviewPickerComponent
        v-if="isFeeInvoiceNotificationTemplate"
        v-model="selectedFeeInvoiceId"
        :language="selectedLanguage"
        @preview="showFeeInvoicePreview"
        @error="previewError = $event"
      />

      <div v-if="previewError" class="alert alert-warning mb-4">
        <font-awesome-icon
          icon="fa-solid fa-exclamation-triangle"
          class="me-2"
        />
        {{ previewError }}
      </div>

      <div v-if="selectedTemplateKey && currentTemplate" class="card">
        <div
          class="card-header d-flex justify-content-between align-items-center"
        >
          <div>
            <strong>{{ currentTemplate.component }}</strong> /
            {{ currentTemplate.name }}
            <span class="badge bg-secondary ms-2">{{
              selectedLanguage.toUpperCase()
            }}</span>
          </div>
          <div class="d-flex gap-2">
            <button
              class="btn btn-sm btn-outline-primary"
              @click="showSendTestModal = true"
              title="Send test email"
            >
              <font-awesome-icon icon="fa-solid fa-envelope" class="me-1" />
              Send Test
            </button>
            <button
              class="btn btn-sm btn-outline-secondary"
              @click="openInNewTab"
              title="Open in new tab"
            >
              <font-awesome-icon
                icon="fa-solid fa-external-link-alt"
                class="me-1"
              />
              Open in New Tab
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <iframe
            ref="emailFrame"
            :srcdoc="currentTemplate.html_content"
            class="email-preview-iframe"
            title="Email Preview"
            sandbox="allow-same-origin"
            @load="resizeIframe"
          ></iframe>
        </div>
      </div>

      <div v-else class="alert alert-info">
        <font-awesome-icon icon="fa-solid fa-info-circle" class="me-2" />
        Please select a template and language to preview.
      </div>

      <!-- Send Test Email Modal -->
      <div
        v-if="showSendTestModal"
        class="modal show d-block"
        tabindex="-1"
        @click.self="showSendTestModal = false"
      >
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Send Test Email</h5>
              <button
                type="button"
                class="btn-close"
                @click="showSendTestModal = false"
              ></button>
            </div>
            <div class="modal-body">
              <p class="text-muted mb-3">
                Send a test email for
                <strong>{{ currentTemplate?.name }}</strong> in
                <strong>{{ selectedLanguage.toUpperCase() }}</strong>
              </p>
              <div class="mb-3">
                <label for="testEmail" class="form-label">Email Address</label>
                <input
                  id="testEmail"
                  v-model="testEmail"
                  type="email"
                  class="form-control"
                  placeholder="your@email.com"
                  required
                />
              </div>
              <div v-if="sendTestError" class="alert alert-danger">
                {{ sendTestError }}
              </div>
              <div v-if="sendTestSuccess" class="alert alert-success">
                <font-awesome-icon
                  icon="fa-solid fa-check-circle"
                  class="me-2"
                />
                Test email sent successfully to {{ testEmail }}!
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                @click="showSendTestModal = false"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="!testEmail || isSendingTest"
                @click="sendTestEmail"
              >
                <span
                  v-if="isSendingTest"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ isSendingTest ? "Sending..." : "Send Test Email" }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showSendTestModal" class="modal-backdrop show"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import FeeInvoicePreviewPickerComponent from "@/admin/FeeInvoicePreviewPickerComponent.vue";
import { formatTransactionDate } from "@/admin/emailTemplateDates";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { computed, inject, onMounted, ref, useTemplateRef, watch } from "vue";

interface RenderedTemplate {
  name: string;
  component: string;
  language: string;
  html_content: string;
  supported_languages: string[];
  path: string;
}

interface Transaction {
  id: number;
  date: string;
  total_amount: number;
  vintage: string;
  num_companies: number;
}

interface Account {
  id: number;
  name: string;
  email: string;
}

interface BuyerInvoice {
  id: number;
  invoice_number: string;
  buyer_name: string;
  created_at: string;
  sales_transaction_id: number;
  is_canceled: boolean;
}

const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;
const globalSuccessHandler = inject("globalSuccessHandler") as (
  title: string,
  description: string
) => void;

const isLoading = ref(true);
const error = ref("");
const renderedTemplates = ref<RenderedTemplate[]>([]);
const selectedTemplateKey = ref("");
const selectedLanguage = ref("en");
const emailFrame = useTemplateRef<HTMLIFrameElement>("emailFrame");
const showSendTestModal = ref(false);
const testEmail = ref("");
const isSendingTest = ref(false);
const sendTestError = ref("");
const sendTestSuccess = ref(false);

// Transaction notification specific state
const transactions = ref<Transaction[]>([]);
const accounts = ref<Account[]>([]);
const selectedTransactionId = ref<number | "">("");
const selectedAccountId = ref<number | "">("");
const isLoadingTransactions = ref(false);
const isLoadingAccounts = ref(false);
const previewError = ref("");

// Buyer sales invoice specific state
const buyerInvoices = ref<BuyerInvoice[]>([]);
const selectedBuyerInvoiceId = ref<number | "">("");
const isLoadingBuyerInvoices = ref(false);

// Fee invoice notification specific state
const selectedFeeInvoiceId = ref<number | "">("");

const uniqueTemplates = computed(() => {
  const seen = new Set<string>();
  const templates: Array<{ name: string; component: string }> = [];

  for (const template of renderedTemplates.value) {
    const key = `${template.component}-${template.name}`;
    if (!seen.has(key)) {
      seen.add(key);
      templates.push({
        name: template.name,
        component: template.component,
      });
    }
  }

  return templates.sort((a, b) => {
    const compA = a.component.localeCompare(b.component);
    return compA !== 0 ? compA : a.name.localeCompare(b.name);
  });
});

const currentTemplate = computed(() => {
  if (!selectedTemplateKey.value) return null;

  const [component, name] = selectedTemplateKey.value.split("-");

  return renderedTemplates.value.find(
    (t) =>
      t.component === component &&
      t.name === name &&
      t.language === selectedLanguage.value
  );
});

const availableLanguagesForCurrentTemplate = computed(() => {
  if (!currentTemplate.value) return [];
  return currentTemplate.value.supported_languages;
});

const isMoneyTemplate = (name: string) =>
  computed(() => selectedTemplateKey.value === `money-${name}`);

const isTransactionNotificationTemplate = isMoneyTemplate(
  "transaction_notification"
);
const isBuyerSalesInvoiceTemplate = isMoneyTemplate("buyer_sales_invoice");
const isFeeInvoiceNotificationTemplate = isMoneyTemplate(
  "fee_invoice_notification"
);

const resizeIframe = () => {
  if (!emailFrame.value) return;

  try {
    const iframeDocument =
      emailFrame.value.contentDocument ||
      emailFrame.value.contentWindow?.document;
    if (iframeDocument) {
      const height = iframeDocument.documentElement.scrollHeight;
      emailFrame.value.style.height = `${Math.max(height + 20, 600)}px`;
    }
  } catch {
    emailFrame.value.style.height = "2000px";
  }
};

const openInNewTab = () => {
  if (!currentTemplate.value) return;

  const newWindow = window.open("", "_blank");
  if (newWindow) {
    newWindow.document.write(currentTemplate.value.html_content);
    newWindow.document.close();
  }
};

const fetchRenderedTemplates = async () => {
  try {
    isLoading.value = true;
    error.value = "";

    const response = await goSolidApi.get<{
      rendered_templates: RenderedTemplate[];
    }>("/api/admin/emails/templates/rendered");

    renderedTemplates.value = response.data.rendered_templates;

    if (renderedTemplates.value.length > 0) {
      const firstTemplate = renderedTemplates.value[0];
      selectedTemplateKey.value = `${firstTemplate.component}-${firstTemplate.name}`;
      selectedLanguage.value = firstTemplate.supported_languages[0] || "en";
    }
  } catch (err: any) {
    error.value = err.message || "Failed to load email templates";
    globalErrorHandler(err);
  } finally {
    isLoading.value = false;
  }
};

const fetchTransactions = async () => {
  try {
    isLoadingTransactions.value = true;
    const response = await goSolidApi.get<{ transactions: Transaction[] }>(
      "/api/admin/emails/transactions"
    );
    transactions.value = response.data.transactions;
  } catch (err: any) {
    globalErrorHandler(err);
  } finally {
    isLoadingTransactions.value = false;
  }
};

const fetchAccounts = async () => {
  if (!selectedTransactionId.value) {
    accounts.value = [];
    return;
  }

  try {
    isLoadingAccounts.value = true;
    const response = await goSolidApi.get<{ accounts: Account[] }>(
      `/api/admin/emails/accounts?transaction_id=${selectedTransactionId.value}`
    );
    accounts.value = response.data.accounts;
  } catch (err: any) {
    globalErrorHandler(err);
  } finally {
    isLoadingAccounts.value = false;
  }
};

const fetchTransactionNotificationPreview = async () => {
  if (!selectedTransactionId.value || !selectedAccountId.value) {
    return;
  }

  try {
    previewError.value = "";
    const response = await goSolidApi.post<{ html_content: string }>(
      "/api/admin/emails/templates/transaction-notification/preview",
      {
        transaction_id: selectedTransactionId.value,
        account_id: selectedAccountId.value,
        language: selectedLanguage.value,
      }
    );

    // Update the current template in the rendered templates array
    const templateIndex = renderedTemplates.value.findIndex(
      (t) =>
        t.component === "money" &&
        t.name === "transaction_notification" &&
        t.language === selectedLanguage.value
    );

    if (templateIndex !== -1) {
      renderedTemplates.value[templateIndex].html_content =
        response.data.html_content;
    } else {
      // Add the template if it doesn't exist
      renderedTemplates.value.push({
        component: "money",
        name: "transaction_notification",
        language: selectedLanguage.value,
        html_content: response.data.html_content,
        supported_languages: ["en", "et"],
        path: "money/transaction_notification.html",
      });
    }
  } catch (err: any) {
    const errorMessage =
      err.response?.data?.message ||
      err.message ||
      "Failed to preview transaction notification";
    previewError.value = errorMessage;
    globalErrorHandler(err);
  }
};

const fetchBuyerInvoices = async () => {
  try {
    isLoadingBuyerInvoices.value = true;
    const response = await goSolidApi.get<{ invoices: BuyerInvoice[] }>(
      "/api/admin/emails/buyer-sales-invoices"
    );
    buyerInvoices.value = response.data.invoices;
  } catch (err: any) {
    globalErrorHandler(err);
  } finally {
    isLoadingBuyerInvoices.value = false;
  }
};

const replaceRenderedTemplate = (name: string, htmlContent: string) => {
  const template = renderedTemplates.value.find(
    (t) =>
      t.component === "money" &&
      t.name === name &&
      t.language === selectedLanguage.value
  );
  if (template) {
    template.html_content = htmlContent;
  }
};

const fetchBuyerSalesInvoicePreview = async () => {
  if (!selectedBuyerInvoiceId.value) {
    return;
  }

  try {
    previewError.value = "";
    const response = await goSolidApi.post<{ html_content: string }>(
      "/api/admin/emails/templates/buyer-sales-invoice/preview",
      { invoice_id: selectedBuyerInvoiceId.value }
    );
    replaceRenderedTemplate("buyer_sales_invoice", response.data.html_content);
  } catch (err: any) {
    previewError.value =
      err.response?.data?.message ||
      err.message ||
      "Failed to preview buyer sales invoice";
    globalErrorHandler(err);
  }
};

const showFeeInvoicePreview = (htmlContent: string) => {
  previewError.value = "";
  replaceRenderedTemplate("fee_invoice_notification", htmlContent);
};

const sendTestEmail = async () => {
  if (!testEmail.value || !currentTemplate.value) return;

  try {
    isSendingTest.value = true;
    sendTestError.value = "";
    sendTestSuccess.value = false;

    const payload: any = {
      template_path: currentTemplate.value.path,
      language: selectedLanguage.value,
      recipient_email: testEmail.value,
    };

    // For transaction notification template, include transaction and account IDs
    if (
      isTransactionNotificationTemplate.value &&
      selectedTransactionId.value &&
      selectedAccountId.value
    ) {
      payload.transaction_id = selectedTransactionId.value;
      payload.account_id = selectedAccountId.value;
    }

    // For buyer sales invoice template, send the email for the selected real invoice
    if (isBuyerSalesInvoiceTemplate.value && selectedBuyerInvoiceId.value) {
      payload.invoice_id = selectedBuyerInvoiceId.value;
    }

    // For fee invoice notification template, send the email for the selected real invoice
    if (isFeeInvoiceNotificationTemplate.value && selectedFeeInvoiceId.value) {
      payload.invoice_id = selectedFeeInvoiceId.value;
    }

    await goSolidApi.post("/api/admin/emails/templates/test", payload);

    sendTestSuccess.value = true;
    globalSuccessHandler("Success", `Test email sent to ${testEmail.value}`);

    setTimeout(() => {
      showSendTestModal.value = false;
      sendTestSuccess.value = false;
      testEmail.value = "";
    }, 2000);
  } catch (err: any) {
    sendTestError.value =
      err.response?.data?.message || err.message || "Failed to send test email";
    globalErrorHandler(err);
  } finally {
    isSendingTest.value = false;
  }
};

// Watch for template selection changes
watch(selectedTemplateKey, (newValue, oldValue) => {
  // The preview error belongs to the template that raised it
  previewError.value = "";

  // Clear transaction notification state when switching away
  if (oldValue && !isTransactionNotificationTemplate.value) {
    selectedTransactionId.value = "";
    selectedAccountId.value = "";
    transactions.value = [];
    accounts.value = [];
  }

  // Fetch transaction data when switching to transaction_notification
  if (isTransactionNotificationTemplate.value && newValue !== oldValue) {
    fetchTransactions();
  }

  // Clear buyer invoice state when switching away, fetch invoices when switching to it
  if (oldValue && !isBuyerSalesInvoiceTemplate.value) {
    selectedBuyerInvoiceId.value = "";
    buyerInvoices.value = [];
  }
  if (isBuyerSalesInvoiceTemplate.value && newValue !== oldValue) {
    fetchBuyerInvoices();
  }
});

// Watch for buyer invoice selection changes
watch(selectedBuyerInvoiceId, (newValue) => {
  if (newValue) {
    fetchBuyerSalesInvoicePreview();
  }
});

// Watch for language changes on the transaction notification template
watch(selectedLanguage, () => {
  if (
    isTransactionNotificationTemplate.value &&
    selectedTransactionId.value &&
    selectedAccountId.value
  ) {
    fetchTransactionNotificationPreview();
  }
});

// Watch for transaction selection changes
watch(selectedTransactionId, (newValue) => {
  // Clear account selection when transaction changes
  selectedAccountId.value = "";

  // Fetch accounts for the new transaction
  if (newValue) {
    fetchAccounts();
  }
});

// Watch for account selection changes
watch(selectedAccountId, (newValue) => {
  if (newValue && selectedTransactionId.value) {
    fetchTransactionNotificationPreview();
  }
});

// Watch for template content changes and resize iframe
watch(currentTemplate, () => {
  setTimeout(resizeIframe, 100);
});

onMounted(() => {
  fetchRenderedTemplates();
});
</script>

<style scoped lang="scss">
.email-preview-iframe {
  width: 100%;
  min-height: 600px;
  border: none;
  background-color: #ffffff;
  display: block;
}

.btn-group {
  .btn {
    flex: 1;
  }
}
</style>
